import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Plus,
    Search,
    Filter,
    Eye,
    Edit3,
    Copy,
    Trash2,
    Share2,
    MoreVertical,
    Calendar,
    Users,
    FileText,
    BarChart3,
    Settings,
    ExternalLink,
    Archive,
    Play,
    Pause,
    Clock,
    CheckCircle,
    XCircle,
    AlertCircle,
    Download
} from 'lucide-react';
import { useForms } from './hooks/useForms';
import { FORM_STATUS } from './config/formTypes';

const FormsList = () => {
    const navigate = useNavigate();
    const {
        forms,
        loading,
        error,
        createForm,
        deleteForm,
        publishForm,
        unpublishForm,
        closeForm,
        archiveForm,
        duplicateForm,
        getUserForms,
        searchForms,
        getFormsByStatus,
        getFormStats,
        getPublicFormUrl,
        getEmbedCode,
        clearError
    } = useForms();

    const [searchTerm, setSearchTerm] = useState('');
    const [selectedStatus, setSelectedStatus] = useState('all');
    const [selectedForm, setSelectedForm] = useState(null);
    const [showDropdown, setShowDropdown] = useState(null);
    const [showShareModal, setShowShareModal] = useState(null);
    const [filteredForms, setFilteredForms] = useState([]);

    // Cargar formularios al iniciar
    useEffect(() => {
        getUserForms();
    }, []);

    // Filtrar formularios cuando cambien los filtros
    useEffect(() => {
        let filtered = forms;

        // Filtrar por estado
        if (selectedStatus !== 'all') {
            filtered = filtered.filter(form => form.status === selectedStatus);
        }

        // Filtrar por búsqueda
        if (searchTerm) {
            filtered = filtered.filter(form =>
                form.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                (form.description && form.description.toLowerCase().includes(searchTerm.toLowerCase()))
            );
        }

        setFilteredForms(filtered);
    }, [forms, selectedStatus, searchTerm]);

    // Crear nuevo formulario
    const handleCreateForm = async () => {
        const newForm = await createForm();
        if (newForm) {
            navigate(`/forms/builder/${newForm.id}`);
        }
    };

    // Eliminar formulario
    const handleDeleteForm = async (formId) => {
        if (window.confirm('¿Estás seguro de que quieres eliminar este formulario?')) {
            await deleteForm(formId);
        }
    };

    // Duplicar formulario
    const handleDuplicateForm = async (formId) => {
        const duplicated = await duplicateForm(formId);
        if (duplicated) {
            navigate(`/forms/builder/${duplicated.id}`);
        }
    };

    // Cambiar estado del formulario
    const handleStatusChange = async (formId, action) => {
        switch (action) {
            case 'publish':
                await publishForm(formId);
                break;
            case 'unpublish':
                await unpublishForm(formId);
                break;
            case 'close':
                await closeForm(formId);
                break;
            case 'archive':
                await archiveForm(formId);
                break;
        }
        setShowDropdown(null);
    };

    // Copiar URL al portapapeles
    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text);
    };

    // Obtener color del estado
    const getStatusColor = (status) => {
        switch (status) {
            case FORM_STATUS.PUBLISHED:
                return 'bg-green-100 text-green-800';
            case FORM_STATUS.DRAFT:
                return 'bg-yellow-100 text-yellow-800';
            case FORM_STATUS.CLOSED:
                return 'bg-red-100 text-red-800';
            case FORM_STATUS.ARCHIVED:
                return 'bg-gray-100 text-gray-800';
            default:
                return 'bg-gray-100 text-gray-800';
        }
    };

    // Obtener ícono del estado
    const getStatusIcon = (status) => {
        switch (status) {
            case FORM_STATUS.PUBLISHED:
                return <CheckCircle className="w-4 h-4" />;
            case FORM_STATUS.DRAFT:
                return <Clock className="w-4 h-4" />;
            case FORM_STATUS.CLOSED:
                return <XCircle className="w-4 h-4" />;
            case FORM_STATUS.ARCHIVED:
                return <Archive className="w-4 h-4" />;
            default:
                return <AlertCircle className="w-4 h-4" />;
        }
    };

    // Obtener texto del estado
    const getStatusText = (status) => {
        switch (status) {
            case FORM_STATUS.PUBLISHED:
                return 'Publicado';
            case FORM_STATUS.DRAFT:
                return 'Borrador';
            case FORM_STATUS.CLOSED:
                return 'Cerrado';
            case FORM_STATUS.ARCHIVED:
                return 'Archivado';
            default:
                return 'Desconocido';
        }
    };

    if (loading && forms.length === 0) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-500 mx-auto mb-4"></div>
                    <p className="text-gray-600">Cargando formularios...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Header */}
            <div className="bg-white border-b border-gray-200">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex items-center justify-between h-16">
                        <div>
                            <h1 className="text-2xl font-bold text-gray-900">Formularios</h1>
                            <p className="text-sm text-gray-600">
                                {filteredForms.length} de {forms.length} formularios
                            </p>
                        </div>

                        <button
                            onClick={handleCreateForm}
                            className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                        >
                            <Plus className="w-5 h-5 mr-2" />
                            Nuevo Formulario
                        </button>
                    </div>
                </div>
            </div>

            {/* Error Alert */}
            {error && (
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
                    <div className="bg-red-50 border border-red-200 rounded-md p-4">
                        <div className="flex">
                            <AlertCircle className="h-5 w-5 text-red-400" />
                            <div className="ml-3">
                                <h3 className="text-sm font-medium text-red-800">Error</h3>
                                <p className="mt-1 text-sm text-red-700">{error}</p>
                            </div>
                            <button
                                onClick={clearError}
                                className="ml-auto text-red-400 hover:text-red-600"
                            >
                                <XCircle className="w-5 h-5" />
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
                {/* Filtros y búsqueda */}
                <div className="mb-6 flex flex-col sm:flex-row gap-4">
                    {/* Barra de búsqueda */}
                    <div className="flex-1">
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                            <input
                                type="text"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                placeholder="Buscar formularios..."
                                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                            />
                        </div>
                    </div>

                    {/* Filtro por estado */}
                    <div className="flex items-center space-x-4">
                        <div className="flex items-center">
                            <Filter className="w-5 h-5 text-gray-400 mr-2" />
                            <select
                                value={selectedStatus}
                                onChange={(e) => setSelectedStatus(e.target.value)}
                                className="border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
                            >
                                <option value="all">Todos los estados</option>
                                <option value={FORM_STATUS.DRAFT}>Borrador</option>
                                <option value={FORM_STATUS.PUBLISHED}>Publicado</option>
                                <option value={FORM_STATUS.CLOSED}>Cerrado</option>
                                <option value={FORM_STATUS.ARCHIVED}>Archivado</option>
                            </select>
                        </div>
                    </div>
                </div>

                {/* Lista de formularios */}
                {filteredForms.length === 0 ? (
                    <div className="text-center py-12 bg-white rounded-lg shadow-sm border border-gray-200">
                        <FileText className="mx-auto h-12 w-12 text-gray-400" />
                        <h3 className="mt-2 text-sm font-medium text-gray-900">
                            {forms.length === 0 ? 'No hay formularios' : 'No se encontraron formularios'}
                        </h3>
                        <p className="mt-1 text-sm text-gray-500">
                            {forms.length === 0
                                ? 'Comienza creando tu primer formulario.'
                                : 'Intenta con diferentes filtros o términos de búsqueda.'
                            }
                        </p>
                        {forms.length === 0 && (
                            <div className="mt-6">
                                <button
                                    onClick={handleCreateForm}
                                    className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700"
                                >
                                    <Plus className="w-5 h-5 mr-2" />
                                    Crear Formulario
                                </button>
                            </div>
                        )}
                    </div>
                ) : (
                    <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                        {filteredForms.map((form) => (
                            <FormCard
                                key={form.id}
                                form={form}
                                onEdit={() => navigate(`/forms/builder/${form.id}`)}
                                onView={() => navigate(`/forms/${form.id}`)}
                                onDuplicate={() => handleDuplicateForm(form.id)}
                                onDelete={() => handleDeleteForm(form.id)}
                                onStatusChange={(action) => handleStatusChange(form.id, action)}
                                onShare={() => setShowShareModal(form)}
                                showDropdown={showDropdown === form.id}
                                setShowDropdown={(show) => setShowDropdown(show ? form.id : null)}
                                getFormStats={() => getFormStats(form.id)}
                            />
                        ))}
                    </div>
                )}
            </div>

            {/* Modal de compartir */}
            {showShareModal && (
                <ShareModal
                    form={showShareModal}
                    onClose={() => setShowShareModal(null)}
                    getPublicFormUrl={getPublicFormUrl}
                    getEmbedCode={getEmbedCode}
                    copyToClipboard={copyToClipboard}
                />
            )}
        </div>
    );
};

// Componente de tarjeta de formulario
const FormCard = ({
    form,
    onEdit,
    onView,
    onDuplicate,
    onDelete,
    onStatusChange,
    onShare,
    showDropdown,
    setShowDropdown,
    getFormStats
}) => {
    const stats = getFormStats();
    const canPublish = form.status === FORM_STATUS.DRAFT;
    const canUnpublish = form.status === FORM_STATUS.PUBLISHED;
    const canClose = form.status === FORM_STATUS.PUBLISHED;
    const canArchive = form.status === FORM_STATUS.CLOSED || form.status === FORM_STATUS.DRAFT;

    const getStatusColor = (status) => {
        switch (status) {
            case FORM_STATUS.PUBLISHED:
                return 'bg-green-100 text-green-800';
            case FORM_STATUS.DRAFT:
                return 'bg-yellow-100 text-yellow-800';
            case FORM_STATUS.CLOSED:
                return 'bg-red-100 text-red-800';
            case FORM_STATUS.ARCHIVED:
                return 'bg-gray-100 text-gray-800';
            default:
                return 'bg-gray-100 text-gray-800';
        }
    };

    const getStatusIcon = (status) => {
        switch (status) {
            case FORM_STATUS.PUBLISHED:
                return <CheckCircle className="w-4 h-4" />;
            case FORM_STATUS.DRAFT:
                return <Clock className="w-4 h-4" />;
            case FORM_STATUS.CLOSED:
                return <XCircle className="w-4 h-4" />;
            case FORM_STATUS.ARCHIVED:
                return <Archive className="w-4 h-4" />;
            default:
                return <AlertCircle className="w-4 h-4" />;
        }
    };

    const getStatusText = (status) => {
        switch (status) {
            case FORM_STATUS.PUBLISHED:
                return 'Publicado';
            case FORM_STATUS.DRAFT:
                return 'Borrador';
            case FORM_STATUS.CLOSED:
                return 'Cerrado';
            case FORM_STATUS.ARCHIVED:
                return 'Archivado';
            default:
                return 'Desconocido';
        }
    };

    return (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
            <div className="p-6">
                {/* Header */}
                <div className="flex items-start justify-between mb-4">
                    <div className="flex-1 min-w-0">
                        <h3 className="text-lg font-semibold text-gray-900 truncate">
                            {form.title}
                        </h3>
                        {form.description && (
                            <p className="text-sm text-gray-600 mt-1 line-clamp-2">
                                {form.description}
                            </p>
                        )}
                    </div>

                    <div className="relative ml-4">
                        <button
                            onClick={() => setShowDropdown(!showDropdown)}
                            className="p-1 text-gray-400 hover:text-gray-600 rounded-md"
                        >
                            <MoreVertical className="w-5 h-5" />
                        </button>

                        {showDropdown && (
                            <div className="absolute right-0 mt-1 w-48 bg-white rounded-md shadow-lg border border-gray-200 z-10">
                                <div className="py-1">
                                    <button
                                        onClick={() => { onEdit(); setShowDropdown(false); }}
                                        className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                    >
                                        <Edit3 className="w-4 h-4 mr-3" />
                                        Editar
                                    </button>

                                    {form.status === FORM_STATUS.PUBLISHED && (
                                        <button
                                            onClick={() => { onView(); setShowDropdown(false); }}
                                            className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                        >
                                            <Eye className="w-4 h-4 mr-3" />
                                            Ver público
                                        </button>
                                    )}

                                    <button
                                        onClick={() => { onDuplicate(); setShowDropdown(false); }}
                                        className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                    >
                                        <Copy className="w-4 h-4 mr-3" />
                                        Duplicar
                                    </button>

                                    {form.status === FORM_STATUS.PUBLISHED && (
                                        <button
                                            onClick={() => { onShare(); setShowDropdown(false); }}
                                            className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                        >
                                            <Share2 className="w-4 h-4 mr-3" />
                                            Compartir
                                        </button>
                                    )}

                                    <div className="border-t border-gray-100 my-1"></div>

                                    {canPublish && (
                                        <button
                                            onClick={() => { onStatusChange('publish'); setShowDropdown(false); }}
                                            className="flex items-center w-full px-4 py-2 text-sm text-green-700 hover:bg-green-50"
                                        >
                                            <Play className="w-4 h-4 mr-3" />
                                            Publicar
                                        </button>
                                    )}

                                    {canUnpublish && (
                                        <button
                                            onClick={() => { onStatusChange('unpublish'); setShowDropdown(false); }}
                                            className="flex items-center w-full px-4 py-2 text-sm text-yellow-700 hover:bg-yellow-50"
                                        >
                                            <Pause className="w-4 h-4 mr-3" />
                                            Despublicar
                                        </button>
                                    )}

                                    {canClose && (
                                        <button
                                            onClick={() => { onStatusChange('close'); setShowDropdown(false); }}
                                            className="flex items-center w-full px-4 py-2 text-sm text-red-700 hover:bg-red-50"
                                        >
                                            <XCircle className="w-4 h-4 mr-3" />
                                            Cerrar
                                        </button>
                                    )}

                                    {canArchive && (
                                        <button
                                            onClick={() => { onStatusChange('archive'); setShowDropdown(false); }}
                                            className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                        >
                                            <Archive className="w-4 h-4 mr-3" />
                                            Archivar
                                        </button>
                                    )}

                                    <div className="border-t border-gray-100 my-1"></div>

                                    <button
                                        onClick={() => { onDelete(); setShowDropdown(false); }}
                                        className="flex items-center w-full px-4 py-2 text-sm text-red-700 hover:bg-red-50"
                                    >
                                        <Trash2 className="w-4 h-4 mr-3" />
                                        Eliminar
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Estado */}
                <div className="flex items-center mb-4">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(form.status)}`}>
                        {getStatusIcon(form.status)}
                        <span className="ml-1">{getStatusText(form.status)}</span>
                    </span>
                </div>

                {/* Estadísticas */}
                {stats && (
                    <div className="flex items-center justify-between text-sm text-gray-600 mb-4">
                        <div className="flex items-center">
                            <FileText className="w-4 h-4 mr-1" />
                            <span>{stats.totalFields} campos</span>
                        </div>
                        <div className="flex items-center">
                            <Users className="w-4 h-4 mr-1" />
                            <span>{stats.responseCount} respuestas</span>
                        </div>
                        <div className="flex items-center">
                            <Clock className="w-4 h-4 mr-1" />
                            <span>{stats.estimatedTime} min</span>
                        </div>
                    </div>
                )}

                {/* Footer */}
                <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                    <div className="text-xs text-gray-500">
                        {(() => {
                            try {
                                if (form.updatedAt) {
                                    if (form.updatedAt.toDate && typeof form.updatedAt.toDate === 'function') {
                                        return new Date(form.updatedAt.toDate()).toLocaleDateString('es-ES');
                                    } else if (form.updatedAt instanceof Date) {
                                        return form.updatedAt.toLocaleDateString('es-ES');
                                    } else {
                                        return new Date(form.updatedAt).toLocaleDateString('es-ES');
                                    }
                                }
                                return 'Fecha no disponible';
                            } catch (error) {
                                return 'Fecha no disponible';
                            }
                        })()}
                    </div>

                    <div className="flex items-center space-x-2">
                        <button
                            onClick={onEdit}
                            className="text-sm text-green-600 hover:text-green-700 font-medium"
                        >
                            Editar
                        </button>

                        {form.status === FORM_STATUS.PUBLISHED && (
                            <button
                                onClick={onView}
                                className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                            >
                                Ver
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

// Modal para compartir formulario
const ShareModal = ({ form, onClose, getPublicFormUrl, getEmbedCode, copyToClipboard }) => {
    const [copiedUrl, setCopiedUrl] = useState(false);
    const [copiedEmbed, setCopiedEmbed] = useState(false);

    const formUrl = getPublicFormUrl(form.id);
    const embedCode = getEmbedCode(form.id);

    const handleCopyUrl = () => {
        copyToClipboard(formUrl);
        setCopiedUrl(true);
        setTimeout(() => setCopiedUrl(false), 2000);
    };

    const handleCopyEmbed = () => {
        copyToClipboard(embedCode);
        setCopiedEmbed(true);
        setTimeout(() => setCopiedEmbed(false), 2000);
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
                <div className="p-6">
                    <div className="flex items-center justify-between mb-6">
                        <h3 className="text-lg font-semibold text-gray-900">
                            Compartir: {form.title}
                        </h3>
                        <button
                            onClick={onClose}
                            className="text-gray-400 hover:text-gray-600"
                        >
                            <XCircle className="w-6 h-6" />
                        </button>
                    </div>

                    <div className="space-y-6">
                        {/* URL pública */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                URL pública
                            </label>
                            <div className="flex items-center space-x-2">
                                <input
                                    type="text"
                                    value={formUrl}
                                    readOnly
                                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-sm"
                                />
                                <button
                                    onClick={handleCopyUrl}
                                    className={`px-4 py-2 rounded-md text-sm font-medium ${copiedUrl
                                            ? 'bg-green-100 text-green-700'
                                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                        }`}
                                >
                                    {copiedUrl ? 'Copiado!' : 'Copiar'}
                                </button>
                                <a
                                    href={formUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="p-2 text-blue-600 hover:text-blue-700"
                                    title="Abrir en nueva pestaña"
                                >
                                    <ExternalLink className="w-4 h-4" />
                                </a>
                            </div>
                        </div>

                        {/* Código de embebido */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Código para embeber
                            </label>
                            <div className="space-y-2">
                                <textarea
                                    value={embedCode}
                                    readOnly
                                    rows={3}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-sm font-mono"
                                />
                                <button
                                    onClick={handleCopyEmbed}
                                    className={`px-4 py-2 rounded-md text-sm font-medium ${copiedEmbed
                                            ? 'bg-green-100 text-green-700'
                                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                        }`}
                                >
                                    {copiedEmbed ? 'Copiado!' : 'Copiar código'}
                                </button>
                            </div>
                        </div>

                        {/* Opciones adicionales */}
                        <div className="pt-4 border-t border-gray-200">
                            <h4 className="text-sm font-medium text-gray-700 mb-3">Opciones de compartir</h4>
                            <div className="flex flex-wrap gap-2">
                                <a
                                    href={`https://twitter.com/intent/tweet?text=Completa mi formulario&url=${encodeURIComponent(formUrl)}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-50"
                                >
                                    Twitter
                                </a>
                                <a
                                    href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(formUrl)}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-50"
                                >
                                    Facebook
                                </a>
                                <a
                                    href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(formUrl)}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-50"
                                >
                                    LinkedIn
                                </a>
                                <a
                                    href={`mailto:?subject=${encodeURIComponent(form.title)}&body=${encodeURIComponent('Completa mi formulario: ' + formUrl)}`}
                                    className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-50"
                                >
                                    Email
                                </a>
                            </div>
                        </div>
                    </div>

                    <div className="flex justify-end pt-6 border-t border-gray-200 mt-6">
                        <button
                            onClick={onClose}
                            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                        >
                            Cerrar
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default FormsList;